import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { HandStates } from 'Fingering/phraseFingering';
import { PhaserToReactListeners, RemoveListener } from 'Phaser/EventBus2';
import { useStalledFor } from 'Hooks/useStalledFor';
import { useDidNotLoadMessageAllowed } from 'Hooks/useDidNotLoadMessageAllowed';
import { reportError } from 'Utils/reportError';
import logger from 'Utils/Logger';
import {
  LESSON_DID_NOT_LOAD_MESSAGE,
  NEXT_PHRASE_DID_NOT_LOAD_MESSAGE,
} from 'Components/LessonDidNotLoad';

/** MUS-651: how long a lesson may go (visible time only) with nothing drawn. */
export const LESSON_LOAD_TIMEOUT_MS = 20000;

/** MUS-651: extra chains of tries (from Retry) alive at once for one phrase request. */
const MAX_EXTRA_CHAINS_PER_REQUEST = 2;

/** One of the PhraseManager's requests for a phrase. */
export interface FetchPhraseRequest {
  excludeFirstNoteRest: boolean;
  sequenceId: number;
  handStates?: HandStates;
  transposition?: number;
  /**
   * MUS-651: another try at a request still on its way, from the lesson's
   * Retry. The request keeps going; whichever answers first is used.
   */
  extraTry?: boolean;
  /** The request an extra try was added to. */
  extraFor?: FetchPhraseRequest;
  /**
   * Aborts an extra try's chain: by the second Retry after it, once another
   * chain of the request answers, or when the session changes or the lesson
   * is left.
   */
  signal?: AbortSignal;
}

/** A phrase request on its way, and the play session it is for. */
interface PhraseRequestInFlight {
  sessionId: string | null;
  request: FetchPhraseRequest;
}

/** Phrase requests that failed for good, and the play session they were for. */
export interface FailedPhraseRequests {
  sessionId: string | null;
  requests: FetchPhraseRequest[];
}

export type LessonRetry =
  | { kind: 'wrap-up' }
  | { kind: 'refetch'; requests: FetchPhraseRequest[] }
  | { kind: 'add-tries'; requests: FetchPhraseRequest[] }
  | { kind: 'new-session' }
  | { kind: 'none' };

/**
 * What Retry does on a lesson that did not load: ask for the wrap-up again
 * when the tier-complete screen stalled; ask again for exactly the phrases
 * this session is still waiting on; add a try to the ones still on their way
 * (a slow answer is kept, not thrown away with its session); or, with nothing
 * on screen, nothing failed and nothing on its way, start the tier over in a
 * new session. A session still being
 * created then is replaced (see startNewSession): a request that hangs would
 * otherwise hold the lesson until it timed out.
 */
export function chooseLessonRetry({
  waitingForWrapUp,
  sessionId,
  failed,
  inFlight = [],
  canStartNewSession,
}: {
  waitingForWrapUp: boolean;
  sessionId: string | null;
  failed: FailedPhraseRequests;
  /** This session's phrase requests still on their way (not extra tries). */
  inFlight?: FetchPhraseRequest[];
  canStartNewSession: boolean;
}): LessonRetry {
  if (waitingForWrapUp) return { kind: 'wrap-up' };
  if (sessionId && failed.sessionId === sessionId && failed.requests.length > 0) {
    return { kind: 'refetch', requests: failed.requests };
  }
  if (sessionId && inFlight.length > 0) return { kind: 'add-tries', requests: inFlight };
  if (canStartNewSession) return { kind: 'new-session' };
  return { kind: 'none' };
}

interface LessonLoadGuardInput {
  /** The lesson's committed play session, or null before there is one. */
  sessionId: string | null;
  /** Fetch one phrase and hand it to the scene; rejects when it fails for good. */
  fetchPhrase: (request: FetchPhraseRequest) => Promise<void>;
  /** The tier is won and the wrap-up has not opened yet. */
  waitingForWrapUp: boolean;
  /**
   * Start the tier over in a new session, replacing one still being created;
   * undefined when there is no level to start.
   */
  startNewSession?: () => void;
  askForWrapUp: () => void;
  /** The account's subscription status; a change lifts an entitlement hold. */
  subscriptionStatus?: string | null;
  timeoutMs?: number;
}

interface LessonLoadGuard {
  /** The FetchPhrase handler: fetches, and records a request that failed for good. */
  answerPhraseRequest: (request: FetchPhraseRequest) => Promise<void>;
  showMessage: boolean;
  /** What the message says did not load: the next phrase, or the lesson. */
  message: string;
  /** Undefined when there is nothing a retry could do. */
  retry?: () => void;
}

/**
 * MUS-651: the lesson never sits blank. Until the session's first phrase is
 * drawn (the session not created, a phrase that would not load, a game that
 * never mounted), or while a won tier waits on a wrap-up that does not come,
 * the lesson shows its message once the timeout has passed, or at once when a
 * phrase fetch gave up - never over the offline screen or the paywall.
 */
export const useLessonLoadGuard = ({
  sessionId,
  fetchPhrase,
  waitingForWrapUp,
  startNewSession,
  askForWrapUp,
  subscriptionStatus,
  timeoutMs = LESSON_LOAD_TIMEOUT_MS,
}: LessonLoadGuardInput): LessonLoadGuard => {
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const [drawnForSession, setDrawnForSession] = useState<string | null>(null);
  const [failedForSession, setFailedForSession] = useState<string | null>(null);
  const failedRef = useRef<FailedPhraseRequests>({ sessionId: null, requests: [] });
  const [attempt, setAttempt] = useState(0);
  const inFlightRef = useRef(new Set<PhraseRequestInFlight>());
  // The extra chains of tries on their way for each request, oldest first (at
  // most MAX_EXTRA_CHAINS_PER_REQUEST).
  const extraChainsRef = useRef(new Map<FetchPhraseRequest, AbortController[]>());
  const { allowed, noteFailure } = useDidNotLoadMessageAllowed(subscriptionStatus);

  // MUS-651: extra chains belong to the session they were started for. A new
  // session, or leaving the lesson, aborts the ones still on their way, so
  // their requests do not run on to their timeouts (and get reported).
  useEffect(() => {
    const chains = extraChainsRef.current;
    return () => {
      chains.forEach((requestChains) => requestChains.forEach((chain) => chain.abort()));
      chains.clear();
    };
  }, [sessionId]);

  useEffect(() => {
    const firstPhraseDrawn = () => setDrawnForSession(sessionIdRef.current);
    PhaserToReactListeners.SightReadingFirstPhraseDrawn(firstPhraseDrawn);
    return () => RemoveListener('SightReadingFirstPhraseDrawn', firstPhraseDrawn);
  }, []);

  // Forgets the failure of exactly that request: a newer request for the same
  // sequence id (the scene asking again) keeps its own.
  const forgetFailure = useCallback(
    (requestSessionId: string | null, request: FetchPhraseRequest) => {
      const failed = failedRef.current;
      if (!requestSessionId || failed.sessionId !== requestSessionId) return;
      const stillFailed = failed.requests.filter((entry) => entry !== request);
      if (stillFailed.length === failed.requests.length) return;
      failedRef.current = { sessionId: requestSessionId, requests: stillFailed };
      if (stillFailed.length === 0) setFailedForSession(null);
    },
    [],
  );

  // A request that still fails after its retries is recorded, not dropped.
  // Dropped, it left the PhraseManager waiting on an answer that never came:
  // an empty staff on entry, or a queue that ran dry mid-tier.
  const answerPhraseRequest = useCallback(
    async (request: FetchPhraseRequest) => {
      const requestSessionId = sessionIdRef.current;
      const inFlight: PhraseRequestInFlight = { sessionId: requestSessionId, request };
      inFlightRef.current.add(inFlight);
      try {
        await fetchPhrase(request);
        // The extra try has answered the request (or found it answered): a
        // failure the request recorded meanwhile no longer stands.
        if (request.extraFor && !request.signal?.aborted) {
          forgetFailure(requestSessionId, request.extraFor);
          // The phrase is answered: the request's other extra chains are not
          // needed (left going, their answers are dropped and their timeouts
          // reported).
          extraChainsRef.current
            .get(request.extraFor)
            ?.filter((chain) => chain.signal !== request.signal)
            .forEach((chain) => chain.abort());
        }
        // The request has its answer: its extra chain, if any, is not needed.
        if (!request.extraTry) {
          extraChainsRef.current.get(request)?.forEach((chain) => chain.abort());
          extraChainsRef.current.delete(request);
        }
      } catch (error) {
        // An extra try that fails leaves the outcome to the request it was added to.
        if (request.extraTry) return;
        // Tries the lesson stopped (its session ended, or it was left) did not fail.
        if (axios.isCancel(error)) return;
        if (!requestSessionId || sessionIdRef.current !== requestSessionId) return;
        logger.error(`[phrase-seq] FAILED seq=${request.sequenceId} after retries`, error);
        reportError(error, 'lesson.phraseFetch');
        noteFailure(error);
        const failed = failedRef.current;
        failedRef.current = {
          sessionId: requestSessionId,
          requests: [...(failed.sessionId === requestSessionId ? failed.requests : []), request],
        };
        setFailedForSession(requestSessionId);
      } finally {
        inFlightRef.current.delete(inFlight);
        // An extra chain that ended is no longer one of the request's chains on its way.
        const owner = request.extraFor;
        const chains = owner ? extraChainsRef.current.get(owner) : undefined;
        if (owner && chains) {
          const stillOnTheirWay = chains.filter((chain) => chain.signal !== request.signal);
          if (stillOnTheirWay.length > 0) extraChainsRef.current.set(owner, stillOnTheirWay);
          else extraChainsRef.current.delete(owner);
        }
      }
    },
    [fetchPhrase, noteFailure, forgetFailure],
  );

  const contentOnScreen = !!sessionId && drawnForSession === sessionId;
  const loadFailed = !!sessionId && failedForSession === sessionId;
  const loadStalled = useStalledFor(
    !contentOnScreen,
    timeoutMs,
    `${sessionId ?? 'no-session'}:${attempt}`,
  );
  const wrapUpStalled = useStalledFor(
    waitingForWrapUp,
    timeoutMs,
    `wrap-up:${sessionId ?? 'no-session'}:${attempt}`,
  );
  const showMessage = allowed && (loadFailed || loadStalled || wrapUpStalled);
  // A phrase that failed for good partway through the tier, with the staff
  // still showing the phrases before it, is the next phrase that did not load.
  const message =
    loadFailed && contentOnScreen && !wrapUpStalled
      ? NEXT_PHRASE_DID_NOT_LOAD_MESSAGE
      : LESSON_DID_NOT_LOAD_MESSAGE;

  const choice = (): LessonRetry =>
    chooseLessonRetry({
      waitingForWrapUp: wrapUpStalled,
      sessionId,
      failed: failedRef.current,
      inFlight: [...inFlightRef.current]
        .filter((entry) => entry.sessionId === sessionId && !entry.request.extraTry)
        .map((entry) => entry.request),
      canStartNewSession: !!startNewSession,
    });

  const retry = () => {
    const decided = choice();
    setAttempt((count) => count + 1);
    if (decided.kind === 'wrap-up') askForWrapUp();
    if (decided.kind === 'refetch') {
      failedRef.current = { sessionId: null, requests: [] };
      setFailedForSession(null);
      decided.requests.forEach((request) => {
        void answerPhraseRequest(request);
      });
    }
    if (decided.kind === 'add-tries') {
      // A Retry always sends something new: a fresh extra chain for each
      // request. The one before it is kept (on a very slow server it may be
      // about to answer); the oldest beyond that is aborted (it may have gone
      // out while the connection was dead), so at most two are alive per request.
      decided.requests.forEach((request) => {
        const chains = extraChainsRef.current.get(request) ?? [];
        const tooMany = chains.length >= MAX_EXTRA_CHAINS_PER_REQUEST;
        if (tooMany) chains[0].abort();
        const chain = new AbortController();
        extraChainsRef.current.set(request, [...(tooMany ? chains.slice(1) : chains), chain]);
        void answerPhraseRequest({
          ...request,
          extraTry: true,
          extraFor: request,
          signal: chain.signal,
        });
      });
    }
    if (decided.kind === 'new-session') startNewSession?.();
  };

  return {
    answerPhraseRequest,
    showMessage,
    message,
    retry: choice().kind === 'none' ? undefined : retry,
  };
};
